/**
 * Side drawer component
 *
 * @module side-drawer
 * @version 1.0.0
 * @extends HTMLElement
 */

customElements.component = "side-drawer";

if (!customElements.get(customElements.component)) {
  class SideDrawer extends HTMLElement {
    static get observedAttributes() {
      return ["side", "width", "animation-duration"];
    }

    /**
     * Web component constructor
     *
     * @constructor
     */
    constructor() {
      super();

      this.style.display = "none";

      // Default configuration
      this.config = {
        side: "left",
        width: "375px",
        animationDuration: 300,
        overlayOpacity: 0.3,
      };

      // Callback functions
      this.afterOpen = null;
      this.beforeClose = null;

      // Store original scroll position
      this.scrollPosition = 0;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.classList.add("side-drawer");
      this.setAttribute("role", "dialog");
      this.setAttribute("aria-modal", "true");
      this.setAttribute("aria-hidden", "true");
      this.render();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;

      switch (name) {
        case "side":
          this.config.side = newValue;
          this.setAttribute("data-side", newValue);
          break;
        case "width":
          this.config.width = newValue;
          this.querySelector("[data-container]").style.width = newValue;
          break;
        case "animation-duration":
          this.config.animationDuration = parseInt(newValue);
          break;
      }
    }

    /**
     * Render component in the DOM
     *
     * @returns {undefined}
     */
    render() {
      // Render template

      let html = this.template();

      html = html.replace(/{{content}}/g, this.innerHTML);

      this.innerHTML = html;

      // After render template

      this.initEvents();
    }

    /**
     * Component template
     *
     * @returns {undefined}
     */
    template() {
      if (window.templateSideDrawer) {
        return window.templateSideDrawer;
      } else {
        return `
                  <div data-overlay aria-hidden="true"></div>
                  <div data-container>
                      <button data-close aria-label="Close drawer">
                          <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21.373 4.45312L13.1416 12.2266L21.373 20L20 21.4531L11.6865 13.6006L3.37305 21.4531L2 20L10.2305 12.2266L2 4.45312L3.37305 3L11.6865 10.8516L20 3L21.373 4.45312Z" fill="currentColor" stroke="transparent"/>
                          </svg>
                          Close
                      </button>
                      <div data-content>{{content}}</div>
                  </div>
              `;
      }
    }

    /**
     * Init component events
     *
     * @returns {undefined}
     */
    initEvents() {
      this.initFocusTrap();

      this.initTriggers();

      // Close on press escape
      window.addEventListener("escape", () => {
        this.close();
      });
    }

    /**
     * Init triggers
     *
     * @returns {undefined}
     */
    initTriggers() {
      if (this.id) {
        document
          .querySelectorAll('[data-drawer-target="' + this.id + '"]')
          .forEach((element) => {
            element.addEventListener("click", this.open.bind(this));
          });
      }

      this.querySelector("[data-overlay]").addEventListener(
        "click",
        this.close.bind(this)
      );

      this.querySelector("[data-close]").addEventListener(
        "click",
        this.close.bind(this)
      );
    }

    /**
     * Check if the drawer is open
     *
     * @returns {boolean}
     */
    isOpen() {
      return this.classList.contains("active") ? true : false;
    }

    /**
     * Disable scroll on html and body
     *
     * @returns {undefined}
     */
    disableScroll() {
      this.scrollPosition = window.scrollY || window.pageYOffset || 0;
      this.previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      document.documentElement.classList.add("scroll-locked");
      document.body.classList.add("scroll-locked");
      document.body.style.setProperty("--scroll-lock-offset", `-${this.scrollPosition}px`);
    }

    /**
     * Enable scroll on html and body
     *
     * @returns {undefined}
     */
    enableScroll() {
      const y = this.scrollPosition || 0;
      document.documentElement.classList.remove("scroll-locked");
      document.body.classList.remove("scroll-locked");
      document.body.style.removeProperty("--scroll-lock-offset");
      if (window.scrollY !== y) {
        window.scrollTo(0, y);
      }
      document.documentElement.style.scrollBehavior = this.previousScrollBehavior || "";
    }

    /**
     * Open drawer
     *
     * @returns {undefined}
     */
    open(event) {
      if (event) event.preventDefault();

      if (!this.isOpen()) {
        this.disableScroll();
        this.style.display = "";
        this.setAttribute("aria-hidden", "false");

        setTimeout(() => {
          this.classList.add("active");
          this.classList.add("transition-in");
        }, 100);

        setTimeout(() => {
          this.classList.remove("transition-in");
          this.activeFocusTrap();
          // Execute afterOpen callback if it exists
          if (typeof this.afterOpen === "function") {
            this.afterOpen();
          }
        }, this.config.animationDuration);
      }
    }

    /**
     * Close drawer
     *
     * @returns {undefined}
     */
    close(event) {
      if (event) event.preventDefault();

      if (this.isOpen()) {
        // Execute beforeClose callback if it exists
        if (typeof this.beforeClose === "function") {
          this.beforeClose();
        }

        this.classList.add("transition-out");
        this.disableFocusTrap();

        // Remove focus from any element inside the drawer
        if (document.activeElement && this.contains(document.activeElement)) {
          document.activeElement.blur();
        }

        setTimeout(() => {
          this.classList.remove("transition-out");
          this.classList.remove("active");
          // Set aria-hidden after transition and focus management
          this.setAttribute("aria-hidden", "true");
          this.enableScroll();
        }, this.config.animationDuration);

        setTimeout(() => {
          this.style.display = "none";
        }, this.config.animationDuration + 100);
      }
    }

    /**
     * Init focus trap
     *
     * @returns {undefined}
     */
    initFocusTrap() {
      if (focusTrap) {
        this.focusTrap = focusTrap.createFocusTrap(this, {
          clickOutsideDeactivates: true,
        });
      }
    }

    /**
     * Disable focus trap
     *
     * @returns {undefined}
     */
    disableFocusTrap() {
      if (this.focusTrap) {
        this.focusTrap.deactivate();
      }
    }

    /**
     * Active focus trap
     *
     * @returns {undefined}
     */
    activeFocusTrap() {
      if (this.focusTrap) {
        this.focusTrap.activate();
      }
    }

    /**
     * Disable the side drawer functionality
     * Removes event listeners and prevents opening
     *
     * @returns {undefined}
     */
    disable() {
      // this.classList.add('disabled');
    }

    /**
     * Enable the side drawer functionality
     * Restores event listeners and allows opening
     *
     * @returns {undefined}
     */
    enable() {
      // this.classList.remove('disabled');
    }
  }

  customElements.define(customElements.component, SideDrawer);
}
